// One call from the history (/history/<id>): who was on it and what it was
// for, the word wall (what went on, the lines it turned on sized by how much
// they mattered, what was decided, what comes next, and the take), the notes,
// the action items, and the transcript. The same call Seamless's Calls screen
// opens, from the same store (worker/src/archive.ts). Owner only: the wall
// never reaches a guest's screen.

import { useEffect, useState, type JSX } from "react";
import { HISTORY_KINDS, type CallWall, type HistoryDetail, type HistoryKind } from "@breeze/core";
import { api, ApiError } from "../lib/api";
import { goBack } from "../lib/nav";
import { ownRoomPath } from "../lib/storage";
import { callWhen, namesLine } from "../lib/format";
import { BackIcon } from "../components/icons";
import { renderNotes } from "./RecordingScreen";
import { wallStyles } from "../lib/wallStyle";

const KIND_LABEL: Record<HistoryKind, string> = { business: "Business", mentorship: "Mentorship", family: "Family", other: "Other" };

export function HistoryScreen({ id }: { readonly id: string }): JSX.Element {
  const [detail, setDetail] = useState<HistoryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kindError, setKindError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api<HistoryDetail>(`/api/history/${id}`, { signal: controller.signal })
      .then(setDetail)
      .catch((err: unknown) => {
        if ((err as Error).name === "AbortError") return;
        setError(err instanceof ApiError ? err.message : "Could not load the call.");
      });
    return () => controller.abort();
  }, [id]);

  const setKind = (kind: HistoryKind): void => {
    if (!detail || detail.kind === kind) return;
    const before = detail.kind;
    setDetail({ ...detail, kind });
    setKindError(null);
    api(`/api/history/${id}`, { method: "PATCH", body: { kind } }).catch((err: unknown) => {
      setDetail((d) => (d ? { ...d, kind: before } : d));
      setKindError(err instanceof ApiError ? err.message : "Could not save that.");
    });
  };

  const remove = (): void => {
    setDeleting(true);
    setDeleteError(null);
    api(`/api/history/${id}`, { method: "DELETE" })
      .then(() => goBack())
      .catch((err: unknown) => {
        setDeleting(false);
        setDeleteError(err instanceof ApiError ? err.message : "Could not delete that.");
      });
  };

  const meta = detail ? [callWhen(detail.started), detail.minutes ? `${detail.minutes} min` : "", namesLine(detail.people)].filter(Boolean).join(" · ") : "";

  return (
    <div className="page history">
      <div className="page__head">
        <a className="btn btn--ghost btn--icon" href={ownRoomPath()} onClick={(e) => { e.preventDefault(); goBack(); }} aria-label="Back" style={{ width: "var(--tap-min)", height: "var(--tap-min)", marginLeft: -10 }}>
          <BackIcon size={22} />
        </a>
        <h1 className="page__title" style={{ fontSize: 20 }}>
          {detail?.title || "Call"}
        </h1>
      </div>

      {error ? <div className="notice notice--danger">{error}</div> : null}
      {!detail && !error ? <div className="skeleton" style={{ height: 220 }} /> : null}

      {detail ? (
        <>
          <div className="history__top">
            <span className="muted">{meta}</span>
            {detail.watchUrl ? (
              <a className="btn" href={detail.watchUrl} target="_blank" rel="noreferrer">
                Watch
              </a>
            ) : null}
          </div>

          <div className="seg seg--4" role="radiogroup" aria-label="Kind of call">
            {HISTORY_KINDS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={detail.kind === k} className="seg__option" onClick={() => setKind(k)}>
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
          {kindError ? <div className="notice notice--danger">{kindError}</div> : null}

          {detail.kind === "business" ? (
            <>
              <Sentences heading="Takeaways" lines={detail.takeaways} />
              <Sentences heading="Learned" lines={detail.learned} />
            </>
          ) : null}

          {detail.wall ? <Wall wall={detail.wall} /> : detail.purpose ? <p className="history__gist">{detail.purpose}</p> : null}

          {detail.summary ? (
            <section className="section">
              <h2 className="section__heading">Notes</h2>
              <div className="notes">{renderNotes(detail.summary)}</div>
            </section>
          ) : null}

          {detail.actionItems.length > 0 ? (
            <section className="section">
              <h2 className="section__heading">Action items</h2>
              <ul className="notes__list">
                {detail.actionItems.map((a, i) => (
                  <li key={i} className={a.done ? "history__done" : undefined}>
                    {a.description}
                    {a.assignee ? <span className="muted"> ({a.assignee})</span> : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {detail.transcript.length > 0 ? (
            <section className="section">
              <h2 className="section__heading">Transcript</h2>
              <div className="transcript">
                {detail.transcript.map((line, i) => (
                  <div key={i} className="transcript__line">
                    <span className="transcript__time">{line.at.replace(/^00:/, "")}</span>
                    <span>
                      {i === 0 || detail.transcript[i - 1]!.speaker !== line.speaker ? <span className="transcript__speaker">{line.speaker} </span> : null}
                      {line.text}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="section history__remove" data-testid="history-remove">
            {deleteError ? <div className="notice notice--danger">{deleteError}</div> : null}
            {confirming ? (
              <>
                <p className="muted">Delete this call, its recording, and its transcript? This can't be undone.</p>
                <div className="history__remove-row">
                  <button type="button" className="btn" onClick={() => setConfirming(false)} disabled={deleting}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn--danger" data-testid="history-delete-confirm" onClick={remove} disabled={deleting}>
                    {deleting ? "Deleting" : "Delete"}
                  </button>
                </div>
              </>
            ) : (
              <button type="button" className="btn btn--danger" data-testid="history-delete" onClick={() => setConfirming(true)}>
                Delete recording
              </button>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}

/** A business call's takeaways, or what was learned about the other side;
 * nothing at all until they are written. */
function Sentences({ heading, lines }: { readonly heading: string; readonly lines: readonly string[] | undefined }): JSX.Element | null {
  if (!lines?.length) return null;
  return (
    <section className="section">
      <h2 className="section__heading">{heading}</h2>
      <ul className="notes__list">
        {lines.map((l, i) => (
          <li key={i}>{l}</li>
        ))}
      </ul>
    </section>
  );
}

/** The word wall: size, face, weight, and color per item from wallStyles. */
function Wall({ wall }: { readonly wall: CallWall }): JSX.Element {
  const styles = wallStyles(wall.items);
  return (
    <section className="wall">
      <p className="history__gist">{wall.gist}</p>
      {wall.items.length > 0 ? (
        <div className="wall__cloud">
          {wall.items.map((it, i) =>
            it.kind === "quote" ? (
              <figure key={i} className={styles[i]}>
                <blockquote>“{it.text}”</blockquote>
                {it.who ? <figcaption>{it.who}</figcaption> : null}
              </figure>
            ) : (
              <span key={i} className={styles[i]}>
                {it.text}
              </span>
            ),
          )}
        </div>
      ) : null}
      {wall.decided.length > 0 || wall.next.length > 0 ? (
        <div className="wall__cols">
          {wall.decided.length > 0 ? (
            <div>
              <h2 className="section__heading">Decided</h2>
              <ul className="wall__list wall__list--decided">
                {wall.decided.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {wall.next.length > 0 ? (
            <div>
              <h2 className="section__heading">Next</h2>
              <ul className="wall__list">
                {wall.next.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
      {wall.take ? (
        <div>
          <h2 className="section__heading">Take</h2>
          <div className="notes">
            {wall.take.split(/\n\s*\n/).map((p, i) => (
              <p key={i} className="notes__para">
                {p}
              </p>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
